'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import { ChevronThinIcon } from '@/components/icons/chevron-thin-icon';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';
import {
  BLOG_CATEGORIES,
  BLOG_CATEGORY_KEYS,
  TRACKED_EVENT_POSITION,
  type BlogCategory,
} from '@/constants';
import {
  NO_ACTIVE_OPTION,
  useCategoryListbox,
} from './useCategoryListbox.hook';

/** 오버레이가 갈리는 지점 — 레포의 `bottom-sheet-modal`·`inquiry-funnel` 과 같은 768px */
const DESKTOP_VIEWPORT = '(min-width: 768px)';

/**
 * 포인터 사용자용 항목 표시 — 하이라이트 링은 키보드 전용이라 hover 는 이 배경만 쓴다.
 *
 * ⚠️ `@media (hover: hover)` 를 벗기지 말 것 — 터치에서 `:hover` 는 탭 뒤에도 남아서,
 * 항목을 탭하면 시트가 닫히는 동안 그 행이 강조된 채로 보인다.
 */
const HOVER_ROW = '[@media(hover:hover)]:hover:bg-3b-background-light';

// 모바일에서만 받는 청크다 — `vaul` 을 데스크톱 번들에 넣지 않으려고 지연 로드한다
const CategorySheet = dynamic(() => import('./category-sheet'), {
  ssr: false,
  loading: () => null,
});

/**
 * 검색 범위(블로그 카테고리)를 고르는 칩.
 *
 * ⚠️ **입력은 한 벌만 렌더하고 오버레이만 뷰포트로 갈라진다.** 데스크톱은 칩 아래 붙는
 * 드롭다운, 모바일은 바텀시트다. 검색어 state 를 든 `BlogSearch` 를 뷰포트별로 두 벌
 * 렌더하면 두 인스턴스의 입력이 갈려 창 크기만 바꿔도 방금 친 글자가 사라진 것처럼
 * 보인다 — 그래서 분기는 이 컴포넌트 안에서만 한다.
 *
 * ARIA 는 `aria-haspopup="listbox"` 로 여는 리스트박스다(값 하나를 고르는 위젯이라
 * "메뉴"가 아니다). 열리면 옵션이 아니라 **리스트박스 자신**이 DOM 포커스를 받고,
 * `aria-activedescendant` 로 활성 옵션을 알린다 — 그래서 옵션은 포커스 가능한 요소가
 * 아니고, 화살표 키 처리도 옵션이 아니라 리스트박스(`useCategoryListbox`)가 갖는다.
 */
export default function CategorySelect({
  value,
  onChange,
}: {
  value: BlogCategory;
  onChange: (next: BlogCategory) => void;
}) {
  const t = useTranslations('Blog.main');
  const tSearch = useTranslations('Blog.landing');
  const isDesktop = useMediaQuery(DESKTOP_VIEWPORT);
  /**
   * ⚠️ `useMediaQuery` 는 SSR·하이드레이션 첫 렌더에서 **항상 false** 다. 그래서 이 게이트가
   * 없으면 데스크톱도 첫 렌더에서 시트 분기를 타고, `next/dynamic` 은 렌더를 시도한 그 순간
   * `import()` 를 발동시켜 **데스크톱이 vaul 청크를 받아 간다**(지연 로드의 목적이 무력화된다).
   * 마운트 뒤에는 `isDesktop` 이 실제 값이라 필요한 쪽만 렌더된다.
   */
  const [mounted, setMounted] = useState(false);
  const menuId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const addOptionGtag = useAddClickButtonGtag({
    label: 'blog_search_category',
    position: TRACKED_EVENT_POSITION.MAIN_HEADER,
  });

  const selectedIndex = BLOG_CATEGORIES.indexOf(value);
  const selectedLabel = t(
    `categories.${BLOG_CATEGORY_KEYS[selectedIndex]}.label`,
  );

  const {
    open,
    hasOpened,
    highlightedIndex,
    keyboardActive,
    highlightByPointer,
    triggerRef,
    setListboxNode,
    toggle,
    close,
    dismiss,
    onTriggerKeyDown,
    onListboxKeyDown,
  } = useCategoryListbox({
    selectedIndex,
    optionCount: BLOG_CATEGORIES.length,
  });

  useEffect(() => setMounted(true), []);

  // 드롭다운 바깥을 누르면 닫는다 — 클릭 대상이 포커스 불가 요소면 blur 가 안 뜨므로
  // (예: 일반 `<div>`) document 레벨 리스너가 필요하다(레포 `use-mega-menu` 와 같은 패턴).
  // 바텀시트는 vaul 이 처리하므로 데스크톱에서만 건다
  useEffect(() => {
    if (!open || !isDesktop) return;

    const onPointerDown = (event: PointerEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) return;
      dismiss();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss 는 매 렌더 새 참조라 넣으면 재구독만 늘어난다
  }, [open, isDesktop]);

  // 마우스 클릭·키보드(Enter/Space) 두 경로가 "카테고리 선택" 을 각자 구현하면 한쪽만
  // gtag 를 빼먹기 쉽다 — 한 함수로 합쳐 공유한다
  const handleSelect = (category: BlogCategory) => {
    addOptionGtag(category);
    onChange(category);
    close();
  };

  const optionId = (category: BlogCategory) => `${menuId}-option-${category}`;

  /** 드롭다운·바텀시트가 공유하는 옵션 목록. 행 높이·글자 크기만 호출부가 정한다 */
  const optionList = (rowClassName: string) => (
    <ul
      ref={setListboxNode}
      id={menuId}
      role="listbox"
      tabIndex={-1}
      aria-label={tSearch('searchCategory')}
      aria-activedescendant={
        highlightedIndex === NO_ACTIVE_OPTION
          ? undefined
          : optionId(BLOG_CATEGORIES[highlightedIndex])
      }
      onKeyDown={event =>
        onListboxKeyDown(event, index => handleSelect(BLOG_CATEGORIES[index]))
      }
      className="outline-none"
    >
      {BLOG_CATEGORIES.map((category, index) => {
        const selected = index === selectedIndex;
        const highlighted = index === highlightedIndex;

        return (
          <li
            key={category}
            id={optionId(category)}
            role="option"
            aria-selected={selected}
            data-index={index}
            onMouseEnter={() => highlightByPointer(index)}
            onClick={() => handleSelect(category)}
            className={`flex w-full cursor-pointer items-center justify-center whitespace-nowrap font-semibold transition-colors ${HOVER_ROW} ${
              // 링은 키보드 이동에만 — 터치 탭이 발생시키는 mouseenter 로는 켜지 않는다
              keyboardActive && highlighted
                ? 'ring-2 ring-inset ring-3b-blue-light-900'
                : ''
            } ${selected ? 'text-3b-blue-light-600' : 'text-3b-text-primary'} ${rowClassName}`}
          >
            {t(`categories.${BLOG_CATEGORY_KEYS[index]}.label`)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      {/* 보이는 글자("전체")만으로는 무엇을 고르는 칩인지 알 수 없어 접근 이름에 맥락을
          덧붙인다. 보이는 텍스트를 포함하므로 WCAG 2.5.3(Label in Name)을 지킨다.
          `type="button"` 은 생략하면 안 된다 — 이 칩은 검색 `form` 안에 있어 기본값
          `submit` 으로 폼이 제출된다 */}
      <TrackedButton
        ref={triggerRef}
        type="button"
        // 여닫기가 한 버튼이라 label 을 갈라야 "열었다" 지표에 닫기 클릭이 섞이지 않는다.
        // `open` 은 클릭 직전 값이므로 이게 곧 이번 클릭이 하는 동작이다
        label={
          open ? 'blog_search_category_close' : 'blog_search_category_open'
        }
        position={TRACKED_EVENT_POSITION.MAIN_HEADER}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={tSearch('searchCategorySelected', {
          category: selectedLabel,
        })}
        onClick={toggle}
        onKeyDown={onTriggerKeyDown}
        className="focus-ring-tight flex h-[26px] shrink-0 items-center rounded-full bg-3b-background-medium px-1.5 text-body2 font-semibold text-[#6c7380] transition-colors hover:bg-3b-background-200 md:h-8 md:gap-1 md:text-body1"
      >
        <span className="pl-[4px]">{selectedLabel}</span>
        <ChevronThinIcon
          direction="down"
          className={`size-[18px] transition-transform motion-reduce:transition-none md:size-5 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </TrackedButton>

      {!mounted
        ? null
        : isDesktop
          ? open && (
              // 칩 기준 좌측 정렬 + pill 바닥에서 8px 아래(시안 실측: pill 48 / 드롭다운 top 56.6).
              // 칩이 pill 안에서 세로 중앙이라 칩 바닥에서는 20px 이다
              <div className="absolute left-0 top-full z-[1000] mt-5 w-max min-w-[82px] rounded-[10px] bg-3b-background-surface py-1 shadow-[0px_12px_12px_-8px_rgba(0,0,0,0.08),0px_0px_4px_0px_rgba(0,0,0,0.04)]">
                {optionList('h-10 px-3.5 text-[14px] leading-[14px]')}
              </div>
            )
          : // 처음 열기 전까지는 렌더하지 않는다 — `CategorySheet` 는 next/dynamic 청크라
            // 렌더 트리에 있기만 해도 import() 가 발동한다(open=false 여도 마찬가지).
            // 한 번 열린 뒤에는 계속 렌더해 둔다 — 그래야 닫힐 때 vaul 이 자기 종료
            // 애니메이션을 끝까지 재생한다(중간에 언마운트하면 애니메이션이 끊긴다)
            hasOpened && (
              <CategorySheet
                open={open}
                onOpenChange={next => {
                  if (!next) close();
                }}
                title={tSearch('searchCategory')}
              >
                {optionList('h-[60px] px-5 text-body1')}
              </CategorySheet>
            )}
    </div>
  );
}
